import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import Modal from "../../../components/base/Modal";
import { Badge, Button, Table, type TableColumn } from "../../../components/ui";
import { useExpensesAccess } from "../../expenses/hooks/useExpensesAccess";
import { usePurchase, useSuppliers } from "../hooks/useActivos";
import { assetPath } from "../lib/conflicts";
import { formatDate, formatMoney } from "../lib/format";
import { ASSET_STATUS_TONES, FINANCE_STATUS_TONES, categoryLabel, financeNoteLabel, financeStatusLabel, langOf, statusLabel, toneOf } from "../lib/vocab";
import { expensePath } from "../paths";
import type { AssetRow, PurchaseRow } from "../services/activosApi";
import { AttachmentsPanel } from "./AttachmentsPanel";
import { ErrorState, InfoRow, Loader } from "./shared";

// ============================================================================
// Detalle de una compra: factura, piezas, adjuntos y, con costos, montos y el
// estado de Finanzas (PR 4): «Pendiente», «Ligado a Finanzas», «Desligado» o
// «No aplica», con la nota que lo explica. «Ver gasto» sólo aparece si la
// compra tiene gasto y la persona tiene acceso a Finanzas (`expensesAccess`):
// sin gasto ligado (WAYAK, o la integración apagada) ni se pregunta.
// Archivo común, idéntico en CC-CONSOLE y WAYAK.
// ============================================================================

export function PurchaseDetailModal({
  purchaseId,
  canSeeCosts,
  onClose,
  onEdit,
}: {
  purchaseId: string;
  canSeeCosts: boolean;
  onClose: () => void;
  onEdit: (purchase: PurchaseRow) => void;
}) {
  const { t, i18n } = useTranslation("activos");
  const lang = langOf(i18n.language);
  const navigate = useNavigate();
  const purchase = usePurchase(purchaseId);
  const suppliers = useSuppliers({});
  const p = purchase.data;
  const supplier = p?.supplierId ? suppliers.data?.items.find((s) => s.id === p.supplierId)?.name || "—" : "—";
  const expenseId = canSeeCosts && p?.finance?.status === "ligado" ? p.finance.expenseId : null;
  const expensesAccess = useExpensesAccess(Boolean(expenseId));
  const note = financeNoteLabel(p?.finance?.note, lang);

  const columns: TableColumn<AssetRow>[] = [
    { key: "name", header: t("fields.name"), render: (a) => a.name },
    { key: "category", header: t("fields.category"), render: (a) => categoryLabel(a.category, lang) },
    { key: "serial", header: t("fields.serial"), render: (a) => a.serial || "—" },
    { key: "status", header: t("fields.status"), render: (a) => <Badge tone={toneOf(ASSET_STATUS_TONES, a.status)}>{statusLabel(a.status, lang)}</Badge> },
    ...(canSeeCosts ? [{ key: "cost", header: t("fields.unitCost"), align: "right" as const, render: (a: AssetRow) => formatMoney(a.purchase?.unitCost, a.purchase?.currency, lang) }] : []),
  ];

  return (
    <Modal
      title={t("purchaseDetail.title")}
      onClose={onClose}
      width="w-full max-w-4xl"
      buttonAction={
        p ? (
          <Button size="sm" variant="secondary" icon={<i className="ri-edit-2-line" />} onClick={() => onEdit(p)} data-testid="purchase-edit">
            {t("detail.edit")}
          </Button>
        ) : undefined
      }
      content={
        purchase.isLoading ? (
          <Loader />
        ) : purchase.isError || !p ? (
          <ErrorState error={purchase.error} onRetry={() => void purchase.refetch()} />
        ) : (
          <div className="space-y-5" data-testid="purchase-detail">
            <div className="grid grid-cols-1 gap-x-8 sm:grid-cols-2">
              <div>
                <InfoRow label={t("fields.supplier")}>{supplier}</InfoRow>
                <InfoRow label={t("fields.purchaseDate")}>{formatDate(p.date, lang)}</InfoRow>
                <InfoRow label={t("fields.invoice")}>{[p.invoice?.serie, p.invoice?.folio].filter(Boolean).join("-") || "—"}</InfoRow>
                <InfoRow label={t("fields.invoiceUuid")}>{p.invoice?.uuid || "—"}</InfoRow>
              </div>
              {canSeeCosts ? (
                <div data-testid="purchase-costs">
                  <InfoRow label={t("fields.subtotal")}>{formatMoney(p.subtotal, p.currency, lang)}</InfoRow>
                  <InfoRow label={t("fields.tax")}>{formatMoney(p.tax, p.currency, lang)}</InfoRow>
                  <InfoRow label={t("fields.total")}>{formatMoney(p.total, p.currency, lang)}</InfoRow>
                  <InfoRow label={t("fields.finance")}>
                    <span className="inline-flex flex-wrap items-center gap-2">
                      <Badge tone={toneOf(FINANCE_STATUS_TONES, p.finance?.status)}>{financeStatusLabel(p.finance?.status, lang)}</Badge>
                      {expenseId && expensesAccess === "allowed" ? (
                        <Button
                          size="sm"
                          variant="link"
                          icon={<i className="ri-receipt-line" />}
                          onClick={() => {
                            onClose();
                            navigate(expensePath(expenseId));
                          }}
                          data-testid="purchase-view-expense"
                        >
                          {t("purchaseDetail.viewExpense")}
                        </Button>
                      ) : null}
                    </span>
                  </InfoRow>
                  {note ? (
                    <p className="pt-1 text-xs text-content-muted" data-testid="purchase-finance-note">
                      {note}
                    </p>
                  ) : null}
                  <p className="pt-1 text-xs text-content-faint">{t("purchaseDetail.financeHint")}</p>
                </div>
              ) : null}
            </div>
            <div>
              <h3 className="mb-2 border-b border-line pb-2 text-sm font-semibold text-content">{t("purchaseDetail.items", { count: p.assets?.length ?? 0 })}</h3>
              <Table
                columns={columns}
                rows={p.assets || []}
                rowKey={(a) => a.id}
                compact
                emptyText={t("purchaseDetail.noItems")}
                onRowClick={(a) => {
                  onClose();
                  navigate(assetPath(a.id));
                }}
              />
            </div>
            <AttachmentsPanel target={{ purchaseId: p.id }} attachments={p.attachments} />
          </div>
        )
      }
    />
  );
}
